<template>
    <div class="flow-rate-chart fuel-section">
      <div class="fuel-header">
        <div>
          <h5>{{ title }}</h5>
          <span>Avg Flow: <strong>{{ avgFlow }}</strong> {{ avgUnit }}</span>
        </div>
        <div class="fuel-value">
          <strong>{{ engineCons }}</strong>
          <span>{{ consUnit }}</span>
        </div>
      </div>
      <ApexChart
        v-if="chartOption && chartOption.series && chartOption.series.length > 0"
        :element="elementId"
        :chartOption="chartOption"
        :series="chartOption.series"
      />
      <div class="mini-panel">
        <div class="mini-panel__item">
          <span class="mini-panel__label">FM Flowrate</span>
          <span class="mini-panel__value">{{ formatValue(currentFlowrate) }} <span class="mini-panel__unit">L/h</span></span>
        </div>
        <div class="mini-panel__item">
          <span class="mini-panel__label">FM Density</span>
          <span class="mini-panel__value">{{ formatValue(currentDensity, 2) }} <span class="mini-panel__unit">g/cm³</span></span>
        </div>
        <div class="mini-panel__item">
          <span class="mini-panel__label">FM Temp.</span>
          <span class="mini-panel__value">{{ formatValue(currentTemp) }} <span class="mini-panel__unit">°C</span></span>
        </div>
      </div>
    </div>
</template>

<script>
import { formatNum } from '@/util/number';
import { interpolateNulls } from '@/util/summary/series';

export default {
  name: 'FlowRateChart',
  props: {
    series: {
      type: Array,
      default: () => [],
    },
    seriesKey: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      default: '',
    },
    avgFlow: {
      type: [String, Number],
      default: '-',
    },
    engineCons: {
      type: [String, Number],
      default: '-',
    },
    avgUnit: {
      type: String,
      default: 'L/h',
    },
    consUnit: {
      type: String,
      default: 'Liter',
    },
    side: {
      type: String,
      default: 'port',
      validator(value) {
        return ['port', 'starboard', 'center'].includes(value);
      },
    },
    currentFlowrate: {
      type: [String, Number],
      default: '-',
    },
    currentDensity: {
      type: [String, Number],
      default: '-',
    },
    currentTemp: {
      type: [String, Number],
      default: '-',
    },
  },
  computed: {
    elementId() {
      return `flow-rate-chart-${this._uid}`;
    },
    chartOption() {
      if (!this.series || this.series.length === 0) return null;

      const seriesData = this.series.length > 60 ? this.series.slice(-60) : this.series;
      const mappedData = seriesData.map((data) => {
        const raw = data?.fmData?.[this.seriesKey]?.volumeFlowrate;
        return Number.isFinite(Number(raw)) ? Number(raw).toFixed(2) : null;
      });

      const interpolatedData = interpolateNulls(mappedData);
      const chartSeries = [
        { name: `${this.title} (Interpolated)`, data: interpolatedData },
        { name: this.title, data: mappedData },
      ];

      return {
        series: chartSeries,
        chart: {
          height: 60,
          width: '100%',
          type: 'area',
          sparkline: {
            enabled: true,
          },
          group: 'sparklines',
        },
        markers: {
          size: 0,
          hover: {
            size: 4,
          },
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          width: 2,
          curve: 'smooth',
          dashArray: [4, 0],
        },
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.5,
            opacityTo: 0,
          },
        },
        colors: ['rgba(0, 132, 255, 0.4)', '#0084FF'],
        xaxis: {
          type: 'datetime',
          categories: seriesData.map((data) => data.timestamp * 1000),
        },
        tooltip: {
          enabled: true,
          followCursor: true,
          intersect: false,
          enabledOnSeries: [1],
          x: {
            format: 'dd/MM/yy HH:mm',
          },
        },
      };
    },
  },
  methods: {
    formatValue(value, decimals = null) {
      if (value === null || typeof value === 'undefined') return '-';
      const numericValue = Number(value);
      if (Number.isFinite(numericValue)) {
        return decimals === null ? numericValue : formatNum(numericValue, decimals);
      }
      return value;
    },
  },
};
</script>

<style scoped>
.fuel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.fuel-header h5 {
  margin: 0 0 4px;
  font-size: 14px;
}

.fuel-header span {
  font-size: 12px;
  color: #6b7280;
}

.fuel-value {
  text-align: right;
}

.fuel-value strong {
  font-size: 16px;
  color: #1d2433;
}

.fuel-value span {
  font-size: 11px;
  color: #6b7280;
}

.mini-panel {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid #E8ECF3;
}

.mini-panel__item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 6px 8px;
  background: #f9fafb;
  border-radius: 12px;
}

.mini-panel__label {
  font-size: 0.7rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.mini-panel__value {
  font-size: 0.9rem;
  font-weight: 700;
  color: #111827;
}

.mini-panel__unit {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
}
</style>
